// =============================================
// Step4Runner — krok 4: wykonanie. Statusy per akcja (ze store'a — przezywaja
// zmiane zakladki / strony), Stop (miedzy domenami), Ponow per wiersz
// (dialog NS, jesli ns_set jest do wykonania), auto-stop po 3 bledach,
// podsumowanie, "Nowy transfer", "Zobacz w Historii", fallback po zamknieciu
// karty: odtworzenie statusow z dns_change_log.
// =============================================
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Square, RotateCcw, History, AlertTriangle, CheckCircle2, Plus } from "lucide-react";
import type { TransferWizardState } from "@/hooks/useTransferWizard";
import type { PlanRow, RowRunState } from "@/services/cloudflare/transferTypes";
import { listDnsChangeLog } from "@/services/cloudflare/dnsChangeLogService";
import { transferRunStore } from "@/lib/transferRunStore";
import { emptyRowState } from "@/services/cloudflare/transferRunner";
import { RunStatusCell } from "./PlanRowStatusCell";
import { NsConfirmDialog, type NsConfirmRow } from "./NsConfirmDialog";

interface Props {
  wizard: TransferWizardState;
  onShowHistory: () => void;
}

export const Step4Runner = ({ wizard, onShowHistory }: Props) => {
  const { run, stopRun, retryRow, reset } = wizard;
  const plan = run.plan;
  const [retryNs, setRetryNs] = useState<{ key: string; row: NsConfirmRow } | null>(null);
  const [restoring, setRestoring] = useState(false);
  const running = run.status === "running";

  const needsNsDialog = (r: PlanRow, state: RowRunState | undefined) => {
    const a = r.actions[1];
    return a.kind === "ns_set" && a.decision === "run" && state?.ns_set.status !== "ok";
  };

  const onRetry = (r: PlanRow) => {
    const state = run.rows[r.domain.key];
    if (needsNsDialog(r, state)) {
      const a = r.actions[1];
      if (a.kind === "ns_set" && a.decision === "run") setRetryNs({ key: r.domain.key, row: { domain: r.domain.key, currentNs: a.currentNs, newNs: a.ns } });
      return;
    }
    void retryRow(r.domain.key);
  };

  /** Fallback: karta zamknieta w trakcie runu → statusy z Historii. */
  const restoreFromHistory = async () => {
    const keys = transferRunStore.lastRunKeys();
    if (!keys.length) return;
    setRestoring(true);
    try {
      for (const key of keys) {
        const rows = await listDnsChangeLog({ limit: 20, domain: key });
        const st = emptyRowState(key);
        for (const act of ["zone_create", "ns_set", "a_record"] as const) {
          const last = rows.find((x) => x.domain === key && x.action === act);
          if (last) st[act] = { status: last.status === "success" ? "ok" : last.status === "failed" ? "failed" : "skipped", message: last.error ?? undefined };
        }
        transferRunStore.setRow(st);
      }
    } finally {
      setRestoring(false);
    }
  };

  if (!plan) {
    const lastKeys = transferRunStore.lastRunKeys();
    return (
      <div className="space-y-4">
        {lastKeys.length > 0 && Object.keys(run.rows).length === 0 ? (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Ostatni run ({lastKeys.length} domen) został przerwany zamknięciem karty</AlertTitle>
            <AlertDescription className="text-xs space-y-2">
              <p>Akcje, które zdążyły się wykonać, są w Historii. Możesz odtworzyć statusy wierszy albo zacząć nowy transfer.</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => void restoreFromHistory()} disabled={restoring}>{restoring ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <History className="h-4 w-4 mr-2" />}Odtwórz statusy z Historii</Button>
                <Button variant="outline" size="sm" onClick={() => { transferRunStore.clearLastRunKeys(); reset(); }}>Nowy transfer</Button>
              </div>
            </AlertDescription>
          </Alert>
        ) : Object.keys(run.rows).length > 0 ? (
          <>
            <RestoredTable rows={run.rows} />
            <Button variant="outline" onClick={() => { transferRunStore.clearLastRunKeys(); reset(); }}><Plus className="h-4 w-4 mr-2" />Nowy transfer</Button>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Brak aktywnego runu. <Button variant="link" className="px-1" onClick={reset}>Wróć do kroku 1</Button></p>
        )}
      </div>
    );
  }

  const doneCount = Object.values(run.rows).filter((r) => [r.zone_create.status, r.ns_set.status, r.a_record.status].every((s) => s !== "pending" && s !== "running")).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {running ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
            <span className="text-sm">Wykonuję… {doneCount}/{plan.rows.length}</span>
            <Button variant="outline" size="sm" onClick={stopRun} disabled={transferRunStore.isStopRequested()}>
              <Square className="h-4 w-4 mr-2" />{transferRunStore.isStopRequested() ? "Zatrzymam po bieżącej domenie…" : "Stop"}
            </Button>
          </>
        ) : run.summary ? (
          <>
            <CheckCircle2 className={`h-4 w-4 ${run.summary.failed ? "text-amber-400" : "text-emerald-400"}`} />
            <span className="text-sm">Zakończono:</span>
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-400">{run.summary.ok} OK</Badge>
            <Badge variant="outline" className={run.summary.failed ? "border-red-500/40 text-red-400" : ""}>{run.summary.failed} błędów</Badge>
            <Badge variant="outline">{run.summary.skipped} pominiętych</Badge>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={onShowHistory}><History className="h-4 w-4 mr-2" />Zobacz w Historii</Button>
              <Button size="sm" onClick={() => { transferRunStore.clearLastRunKeys(); reset(); }}><Plus className="h-4 w-4 mr-2" />Nowy transfer</Button>
            </div>
          </>
        ) : null}
      </div>

      {run.summary?.stoppedReason === "consecutive_failures" && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Zatrzymano po 3 kolejnych błędach</AlertTitle>
          <AlertDescription className="text-xs">Sprawdź token / konto (Konta → Sprawdź token) i klucze AfterMarket w Coolify. Pozostałe domeny nie były ruszane — po naprawie użyj „Ponów” przy wierszach.</AlertDescription>
        </Alert>
      )}
      {run.summary?.stoppedReason === "user" && (
        <Alert><AlertTitle>Zatrzymano na żądanie</AlertTitle><AlertDescription className="text-xs">Domeny ze statusem „czeka” nie były ruszane.</AlertDescription></Alert>
      )}

      <div className="rounded-lg border overflow-x-auto max-h-[60vh] overflow-y-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead>Domena</TableHead>
              <TableHead>1 · Zona</TableHead>
              <TableHead>2 · NS</TableHead>
              <TableHead>3 · Rekordy A</TableHead>
              <TableHead className="text-right w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {plan.rows.map((r) => {
              const st = run.rows[r.domain.key] ?? emptyRowState(r.domain.key);
              const failed = [st.zone_create.status, st.ns_set.status, st.a_record.status].includes("failed");
              const pending = [st.zone_create.status, st.ns_set.status, st.a_record.status].every((s) => s === "pending");
              const allSkip = r.actions.every((a) => a.decision === "skip");
              return (
                <TableRow key={r.domain.key} className={allSkip ? "opacity-50" : ""}>
                  <TableCell className="font-mono text-sm align-top">{r.domain.key}</TableCell>
                  <TableCell className="align-top"><RunStatusCell status={st.zone_create.status} message={st.zone_create.message} /></TableCell>
                  <TableCell className="align-top"><RunStatusCell status={st.ns_set.status} message={st.ns_set.message} /></TableCell>
                  <TableCell className="align-top"><RunStatusCell status={st.a_record.status} message={st.a_record.message} /></TableCell>
                  <TableCell className="align-top text-right">
                    {!running && !allSkip && (failed || (pending && run.summary)) && (
                      <Button variant="outline" size="sm" className="h-7" onClick={() => onRetry(r)}><RotateCcw className="h-3 w-3 mr-1" />Ponów</Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <NsConfirmDialog
        open={!!retryNs}
        onOpenChange={(o) => { if (!o) setRetryNs(null); }}
        onConfirm={() => { const k = retryNs?.key; setRetryNs(null); if (k) void retryRow(k); }}
        rows={retryNs ? [retryNs.row] : []}
      />
    </div>
  );
};

const RestoredTable = ({ rows }: { rows: Record<string, RowRunState> }) => (
  <div className="rounded-lg border overflow-x-auto">
    <Table>
      <TableHeader><TableRow><TableHead>Domena</TableHead><TableHead>Zona</TableHead><TableHead>NS</TableHead><TableHead>Rekordy A</TableHead></TableRow></TableHeader>
      <TableBody>
        {Object.values(rows).map((st) => (
          <TableRow key={st.key}>
            <TableCell className="font-mono text-sm">{st.key}</TableCell>
            <TableCell><RunStatusCell status={st.zone_create.status} message={st.zone_create.message} /></TableCell>
            <TableCell><RunStatusCell status={st.ns_set.status} message={st.ns_set.message} /></TableCell>
            <TableCell><RunStatusCell status={st.a_record.status} message={st.a_record.message} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);
